import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfig } from '../../../config/ConfigProvider';
import { captureException } from '../../../services/sentryService';
import { CreditCardBrand, CustomerType } from '../../../types';
import AdyenCreditCardForm, { AdyenCreditCardFormRef } from '../../Adyen/Forms/AdyenCreditCardForm';
import { AdyenEnvironment } from '../../Adyen/Forms/AdyenCreditCardForm.static';
import HtmlEmbeddedAlert from '../../Banners/HtmlEmbeddedAlert';
import CoreProviders, { CoreProvidersProps } from '../../CoreProviders';
import { useErrorContext } from '../../ErrorContextProvider';

export interface AdyenCaptureCreditCardsProps extends CoreProvidersProps {
  customerType: CustomerType;
  currency: string;
  isFallbackPsp: boolean;
  adyenEnvironment: AdyenEnvironment;
  supportedCreditCardBrands: CreditCardBrand[];
}

interface InnerAdyenCaptureCreditCardsProps {
  customerType: CustomerType;
  isFallbackPsp: boolean;
  adyenEnvironment: AdyenEnvironment;
  supportedCreditCardBrands: CreditCardBrand[];
}

/**
 * Component to solely capture the credit card information with Adyen.
 * Does not provide a mean to save it: external component must use the globally-exposed 'computeAdyenCreditCardFormData'.
 *
 * To be used on the registration page.
 */
const InnerAdyenCaptureCreditCards: React.FC<InnerAdyenCaptureCreditCardsProps> = ({
  customerType,
  isFallbackPsp,
  adyenEnvironment,
  supportedCreditCardBrands,
}) => {
  const form = useRef<AdyenCreditCardFormRef>(null);
  const { config } = useConfig();
  const { t } = useTranslation();
  const { setError } = useErrorContext();
  const [showForm, setShowForm] = useState(true);

  const onFatalError = useCallback(
    (error: unknown, showForm?: boolean) => {
      captureException({ error });
      setError({
        message: t('checkout.payment.creditcard.error.addcard.apicall'),
      });
      setShowForm(typeof showForm === 'undefined' ? true : showForm);
    },
    [setError],
  );

  return showForm ? (
    <>
      <HtmlEmbeddedAlert innerHtml={t('checkout.payment.creditcard.3d.secure.message')} />
      <AdyenCreditCardForm
        ref={form}
        adyenEnvironment={adyenEnvironment}
        clientKey={config.adyen.clientKey}
        supportedCreditCardBrands={supportedCreditCardBrands}
        onFatalError={onFatalError}
        allowVaulting={!isFallbackPsp && customerType !== 'GUEST_CUSTOMER'}
        labelInline={false}
      />
    </>
  ) : null;
};

/*
 * Wrapper over the true component, because we first need to call the CoreProviders.
 */
const AdyenCaptureCreditCards: React.FC<AdyenCaptureCreditCardsProps> = ({
  env,
  shopBundle,
  locale,
  domain,
  errorContainerId,
  csrfToken,
  customerType,
  isFallbackPsp,
  adyenEnvironment,
  supportedCreditCardBrands,
}) => {
  return (
    <CoreProviders
      env={env}
      shopBundle={shopBundle}
      locale={locale}
      domain={domain}
      errorContainerId={errorContainerId}
      csrfToken={csrfToken}>
      <InnerAdyenCaptureCreditCards
        adyenEnvironment={adyenEnvironment}
        supportedCreditCardBrands={supportedCreditCardBrands}
        isFallbackPsp={isFallbackPsp}
        customerType={customerType}
      />
    </CoreProviders>
  );
};

export default AdyenCaptureCreditCards;
